import {
  APIComponentAttributes,
  ArdoqBlob,
  Described,
  DirectedTripleWithFilters,
  GroupingRule,
  Named,
  ViewIds,
  WithArdoqPersistent,
} from './apiTypes';
import { ArdoqId, Entity, MetaData, Versioned } from './coreTypes';
import { FilterInterfaceFilter, FormattingFilter } from './filterApiTypes';
import { APIResourcePermissionAttributes } from './resourcePermissionsAPITypes';
import { APIScopeData } from './scopeDataAPITypes';
import {
  PathCollapsingRule,
  StartSetExpansion,
  toRequestedPaths,
  TraversalBase,
  TraversalPathMatchingType,
  ViewpointBuilderFilters,
} from './traversal';

/**
 * @deprecated
 *
 * This type should not exist, viewpoint-saving functionality should not care what settings type it
 * gets and should not filter the settings. The settings shape should be correct in the app state
 * and the viewpoint-saving function should persist it as is, without altering it.
 *
 * Furthermore, this type is incorrect, as it lists workspace mode views.
 *
 * This issue is to be addressed in @see https://ardoqcom.atlassian.net/browse/ARD-27919
 */
export type ViewpointSavedViewSettings = Partial<
  Record<ViewIds, Record<string, any>>
>;

export type APIViewpointAttributes = Entity &
  MetaData &
  ArdoqBlob &
  Named &
  Described &
  Versioned &
  WithArdoqPersistent & {
    viewName: ViewIds;
    startType: string;
    paths: DirectedTripleWithFilters[][];
    filters?: ViewpointBuilderFilters;
    conditionalFormatting?: (FormattingFilter | FilterInterfaceFilter)[] | null;
    groupBys?: GroupingRule[] | null;
    viewSettings?: ViewpointSavedViewSettings | null;
    pathMatching: TraversalPathMatchingType;
    pathCollapsingRules?: PathCollapsingRule[];
    /**
     * When set, executing the viewpoint expands the resolved start set with its
     * descendants in the component hierarchy. `startType` then holds the type the
     * expansion starts from: the paths carry the `anyType` sentinel whenever the
     * hierarchy spans several component types, so they cannot supply it.
     *
     * `null` clears a stored expansion — a PUT writes only the keys it carries,
     * so omitting it would keep the stored value.
     */
    startSetExpansion?: StartSetExpansion | null;
  };

export type CreateViewpointAttributes = Pick<
  APIViewpointAttributes,
  | 'name'
  | 'viewName'
  | 'startType'
  | 'paths'
  | 'description'
  | 'groupBys'
  | 'conditionalFormatting'
  | 'filters'
  | 'viewSettings'
  | 'pathMatching'
  | 'pathCollapsingRules'
  | 'startSetExpansion'
>;

export type UpdateTraversalAttributes = CreateViewpointAttributes &
  Entity &
  Versioned;

/**
 * `startSetExpansion` is required rather than optional so that spreading this
 * over a viewpoint overrides the stored value. An optional property in the
 * later spread is treated as possibly absent, which would keep the `null`.
 */
type TraversalBaseFromViewpoint = Omit<TraversalBase, 'startSetExpansion'> & {
  startSetExpansion: StartSetExpansion | undefined;
};

/**
 * Maps a stored viewpoint onto the traversal to execute. Not a plain subset of
 * its attributes: an expansion's `startSetExpansionFromType` is stored in
 * `startType`, and an expansion without paths has to request one empty path.
 */
export const viewpointToTraversalBase = (
  viewpoint: APIViewpointAttributes
): TraversalBaseFromViewpoint => ({
  paths: toRequestedPaths(viewpoint.paths, viewpoint.startSetExpansion),
  filters: viewpoint.filters ?? {},
  pathMatching: viewpoint.pathMatching,
  pathCollapsingRules: viewpoint.pathCollapsingRules ?? [],
  startSetExpansion: viewpoint.startSetExpansion ?? undefined,
  ...(viewpoint.startSetExpansion
    ? { startSetExpansionFromType: viewpoint.startType }
    : {}),
});

export type CreateViewpointInFolderAttributes = CreateViewpointAttributes & {
  folderId: ArdoqId;
};

export type CreateViewpointResponse = {
  traversal: APIViewpointAttributes;
  permission: APIResourcePermissionAttributes;
};

export type UserComponent = APIComponentAttributes & {
  id: undefined;
  _id: ArdoqId;
};

export type APIConnectedUserScopeData = APIScopeData & {
  'user-component': UserComponent;
};
